import { useState } from "react";
import { STICKER_COLORS } from "../lib/stickerPalette";
import { Sun, Moon, Copy, Check } from "lucide-react";

/**
 * Toon het volledige Metamark-kleurenpalet — zowel Mat (M4) als Glanzend (M7).
 * Klant kan toggelen tussen afwerkingen, en op een kleur klikken om de naam + hex te kopiëren.
 */
export default function MetamarkColorPalette() {
    const [finish, setFinish] = useState("mat"); // "mat" | "glanzend"
    const [copied, setCopied] = useState(null);

    const copy = (c) => {
        const txt = `${c.name} — ${c.hex}`;
        navigator.clipboard?.writeText(txt);
        setCopied(c.hex);
        setTimeout(() => setCopied(null), 1500);
    };

    return (
        <div data-testid="metamark-palette" className="rounded-3xl bg-white shadow-[0_8px_32px_rgba(62,39,35,0.06)] p-6 md:p-8 border border-[var(--border-soft)]">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Vinylkleuren · Metamark</p>
                    <h3 className="font-display text-3xl md:text-4xl mt-1 leading-tight">
                        {STICKER_COLORS.length} kleuren — <span className="italic" style={{ color: "var(--rose-deep)" }}>
                            {finish === "mat" ? "M4 Mat" : "M7 Glanzend"}
                        </span>
                    </h3>
                    <p className="text-sm mt-2 max-w-xl" style={{ color: "var(--text-2)" }}>
                        Alle stickers worden gesneden in <strong>één enkele kleur</strong> per ontwerp.
                        Identiek palet voor beide afwerkingen — kies puur op de look die je wil.
                    </p>
                </div>

                {/* Mat / Glanzend toggle */}
                <div className="flex p-1 rounded-full" style={{ background: "var(--bg-warm)" }} data-testid="palette-finish-toggle">
                    <button
                        type="button"
                        data-testid="palette-mat-btn"
                        onClick={() => setFinish("mat")}
                        className={`px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-all ${finish === "mat" ? "bg-white shadow-sm" : ""}`}
                        style={{ color: finish === "mat" ? "var(--text)" : "var(--text-2)" }}>
                        <Moon size={12} /> M4 · Mat
                    </button>
                    <button
                        type="button"
                        data-testid="palette-glans-btn"
                        onClick={() => setFinish("glanzend")}
                        className={`px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-all ${finish === "glanzend" ? "bg-white shadow-sm" : ""}`}
                        style={{ color: finish === "glanzend" ? "var(--text)" : "var(--text-2)" }}>
                        <Sun size={12} /> M7 · Glanzend
                    </button>
                </div>
            </div>

            {/* M7 op bestelling banner */}
            {finish === "glanzend" && (
                <div className="mt-4 flex items-start gap-2.5 p-3 rounded-2xl border" style={{ background: "var(--rose-tint)", borderColor: "var(--rose-soft)" }} data-testid="m7-orderonly-notice">
                    <span className="text-base shrink-0" aria-hidden>⏱</span>
                    <p className="text-xs leading-relaxed" style={{ color: "var(--text)" }}>
                        <strong>M7 Glanzend is enkel op bestelling</strong> — productietijd 3-7 werkdagen.
                        M4 Mat hebben we vaak rechtstreeks op voorraad.
                    </p>
                </div>
            )}

            {/* Grid */}
            <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-3" data-testid="palette-grid">
                {STICKER_COLORS.map((c) => {
                    const isCopied = copied === c.hex;
                    const isLight = ["#FFFFFF", "#FFF44F", "#FFCC00", "#F0EAD6", "#D5C7AD", "#C8C8C8", "#C0C0C0", "#C4D600", "#7AB55C", "#FFA858"].includes(c.hex);
                    return (
                        <button
                            key={c.hex}
                            type="button"
                            data-testid={`color-${c.hex.slice(1)}`}
                            onClick={() => copy(c)}
                            title={`${c.name} — klik om te kopiëren`}
                            className="group flex flex-col items-center gap-1.5 focus:outline-none">
                            <div
                                className={`relative w-full aspect-square rounded-2xl border transition-all group-hover:scale-105 group-hover:shadow-lg ${isLight ? "border-[var(--border)]" : "border-transparent"}`}
                                style={{
                                    background: c.hex,
                                    // Glans effect bij M7
                                    boxShadow: finish === "glanzend"
                                        ? `inset 0 6px 14px rgba(255,255,255,0.45), inset 0 -10px 18px rgba(0,0,0,0.18), 0 2px 8px rgba(62,39,35,0.10)`
                                        : `inset 0 0 0 1px rgba(255,255,255,0.04), 0 1px 3px rgba(62,39,35,0.06)`,
                                }}>
                                {isCopied && (
                                    <div className="absolute inset-0 flex items-center justify-center rounded-2xl backdrop-blur-sm" style={{ background: "rgba(255,255,255,0.85)" }}>
                                        <Check size={20} style={{ color: "var(--success)" }} />
                                    </div>
                                )}
                                <Copy size={11} className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: isLight ? "var(--text-3)" : "rgba(255,255,255,0.9)" }} />
                            </div>
                            <span className="text-[11px] font-medium leading-tight text-center" style={{ color: "var(--text)" }}>{c.name}</span>
                            <span className="font-mono-dm text-[9px]" style={{ color: "var(--text-3)" }}>{c.hex}</span>
                        </button>
                    );
                })}
            </div>

            <p className="text-[11px] mt-5 text-center" style={{ color: "var(--text-3)" }}>
                Klik op een kleur om de naam + hex te kopiëren. Kleuren zijn dichtbenadering — vraag een staal aan voor 100% match.
            </p>
        </div>
    );
}
